import { location, updateURL } from 'cdui-js';
import { debounce } from './utils';
import { stripOverlayMark } from './overlay-history';

/**
 * 用目标 URL 替换当前 history 条目并同步 cdui 路由（不 push）。
 * 成功页「查看记录」等场景勿再用 replaceState + location.routeTo（routeTo 会再 push 一层，
 * iOS WebView canGoBack 时要点两次才能关页）。
 *
 * 同时剥掉当前 state 上的 `__difiOverlay` 弹层 MARK：否则随后弹层 `release()` 会看到 MARK
 * 误以为弹层还在，再 `history.back()` 把刚替换好的 URL 退回去（点「查看记录」不跳转的根因）。
 */
export function routeReplace(url: string, scrollTo?: [number, number]) {
  if (typeof window === 'undefined') return;
  const next = url || '';
  const cleanState = stripOverlayMark(window.history.state as Record<string, unknown> | null);
  window.history.replaceState(cleanState, '', next);
  updateURL(next);
  if (scrollTo) {
    window.scrollTo(scrollTo[0] | 0, scrollTo[1] | 0);
  }
}

/**
 * 是否可安全 `history.back`。
 * Safari / SFSafariViewController / 部分 WebView 的 `history.length` 常 >1 但无可回页；
 * 主流做法：有同域 `document.referrer` 才视为可后退。
 */
export function canSafelyHistoryBack(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.history.length <= 1) return false;
  const ref = document.referrer;
  if (!ref) return false;
  try {
    return new URL(ref).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** 外部浏览器入口页无法 `window.close`（尤其 iOS SFSafariViewController）时的站内兜底 */
export function resolveExternalBrowserExitHref(): string {
  const raw = String(import.meta.env.VITE_BASE_URL || '')
    .trim()
    .replace(/\/$/, '');
  if (/^https?:\/\//i.test(raw)) return `${raw}/`;
  if (raw.startsWith('/')) return `${window.location.origin}${raw}/`;
  return `${window.location.origin}/`;
}

/** 返回上一页；无可回历史则去主站首页（兼容 iOS 系统浏览器 / SFSafariViewController） */
export function goBackByBrowser() {
  if (canSafelyHistoryBack()) {
    window.history.back();
    return;
  }
  try {
    location.routeTo('/');
  } catch {
    window.location.replace(resolveExternalBrowserExitHref());
  }
}

/**
 * H5 禁止浏览器侧滑/后退离开当前页（pushState + popstate 顶回）。
 * 离开页面时必须调用返回的 unlock，否则会污染全局 history。
 */
export function blockBack(): () => void {
  const push = () => {
    window.history.pushState({ __difiBlockBack: 1 }, '', window.location.href);
  };
  push();

  const onPopState = () => {
    push();
  };
  window.addEventListener('popstate', onPopState);

  return () => {
    window.removeEventListener('popstate', onPopState);
  };
}

export type ListenOrientationHandle = {
  /** 移除 resize / orientationchange 监听；已排队防抖不会再触发 callback */
  destroy: () => void;
};

/**
 * 手机横竖屏检测（防抖 + 兼容全机型）
 * @param callback 状态变化回调 (isLandscape: boolean)
 * @param debounceMs 防抖毫秒，默认 100（减轻键盘弹出等误判）
 */
export function listenOrientation(callback: (isLandscape: boolean) => void, debounceMs = 100): ListenOrientationHandle {
  let disposed = false;

  function check() {
    if (disposed) return;
    const isLandscape = window.innerWidth > window.innerHeight;
    callback?.(isLandscape);
  }

  const debouncedCheck = debounce(check, debounceMs);

  check();

  function handler() {
    debouncedCheck();
  }

  window.addEventListener('resize', handler);
  window.addEventListener('orientationchange', handler);

  function destroy() {
    disposed = true;
    window.removeEventListener('resize', handler);
    window.removeEventListener('orientationchange', handler);
  }

  return { destroy };
}

/**
 * 移动端左右滑动监听（左滑 / 右滑）
 * @param {Object} options 配置
 * @param {number} [options.swipeThreshold=50] 滑动有效距离（px）
 * @param {number} [options.verticalThreshold=100] 垂直最大容忍距离
 * @param {number} [options.debounceMs=200] 触发左右滑回调的防抖毫秒（连续滑动只落一次业务）
 * @param {Function} [options.onSwipeRight] 右滑回调 ←
 * @param {Function} [options.onSwipeLeft] 左滑回调 →
 * @returns {Function} 销毁监听的方法
 */
export type UseSwipeOptions = {
  swipeThreshold?: number;
  verticalThreshold?: number;
  /** 左右滑回调防抖间隔（ms），默认 200 */
  debounceMs?: number;
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
};
export function useSwipe(options: UseSwipeOptions = {}) {
  const {
    swipeThreshold = 50,
    verticalThreshold = 100,
    debounceMs = 200,
    onSwipeRight = () => {},
    onSwipeLeft = () => {},
  } = options;

  let disposed = false;
  const fireSwipeRight = debounce(() => {
    if (!disposed) onSwipeRight();
  }, debounceMs);
  const fireSwipeLeft = debounce(() => {
    if (!disposed) onSwipeLeft();
  }, debounceMs);

  let startX = 0;
  let startY = 0;
  let hasTouchStart = false;

  // 触摸开始
  function onTouchStart(e: TouchEvent) {
    hasTouchStart = true;
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
  }

  // 触摸结束
  function onTouchEnd(e: TouchEvent) {
    if (!hasTouchStart) return;

    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;

    const diffX = endX - startX;
    const diffY = endY - startY;

    hasTouchStart = false;

    // 过滤垂直滑动
    if (Math.abs(diffY) > verticalThreshold) {
      startX = startY = 0;
      return;
    }

    // 右滑
    if (diffX > swipeThreshold) {
      fireSwipeRight();
    }
    // 左滑
    else if (diffX < -swipeThreshold) {
      fireSwipeLeft();
    }

    startX = startY = 0;
  }

  // 绑定事件
  document.addEventListener('touchstart', onTouchStart, false);
  document.addEventListener('touchend', onTouchEnd, false);

  // 返回销毁函数
  return function destroy() {
    disposed = true;
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchend', onTouchEnd);
  };
}

export const getBrowserName = () => {
  const { userAgent } = navigator;

  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  if (userAgent.includes('Trident') || userAgent.includes('MSIE')) return 'IE';

  return 'Unknown';
};
